import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  getClubInvitations,
  activateInvitation,
  cancelInvitation,
  type Invitation,
} from "../../services/invitations";
import { formatDate } from "../../lib/dateFormat";

/**
 * Invitații în așteptare — and the two things a club actually wants to do
 * with one.
 *
 * Listed for the whole CLUB rather than for whoever is signed in: the old list
 * showed only invitations you had sent yourself, so an admin could not see
 * their coordinator's — or their co-admin's — and the section looked empty
 * while people were waiting.
 *
 * "Activează" exists because waiting for somebody to find an email is not a
 * workflow a club can run on. The club typed the address and is vouching for
 * it, so the account comes out fully active and the admin passes the password
 * on by whatever means they already use to talk to that person.
 */

const ROLE_LABEL: Record<string, string> = {
  coach: "Antrenor",
  coordinator: "Coordonator",
  athlete: "Sportiv",
  parent: "Părinte",
};

export default function PendingInvitations({
  clubId,
  roles,
  title = "Invitații în așteptare",
  onChanged,
  reloadKey,
}: {
  clubId: string | null | undefined;
  /** Limit to these role codes. Omit for every pending invitation. */
  roles?: string[];
  title?: string;
  onChanged?: () => void;
  /** Change this to force a reload — e.g. after the page sent an invitation. */
  reloadKey?: number | string;
}) {
  const [rows, setRows] = useState<(Invitation & { sport_name?: string | null })[]>([]);
  const [loading, setLoading] = useState(true);

  const [activating, setActivating] = useState<Invitation | null>(null);
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [done, setDone] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(async () => {
    if (!clubId) return;
    setLoading(true);
    try {
      const all = await getClubInvitations(clubId, { status: "pending" });
      setRows(roles && roles.length ? all.filter((i) => roles.includes(i.role_code)) : all);
    } catch {
      setRows([]);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, roles ? roles.join(",") : "", reloadKey]);

  useEffect(() => {
    load();
  }, [load]);

  const openActivate = (inv: Invitation) => {
    setActivating(inv);
    setFullName("");
    setFormError(null);
    // Suggested, not imposed — an admin who wants their own can overwrite it.
    setPassword(suggestPassword());
  };

  const submit = async () => {
    if (!activating) return;
    if (password.trim().length < 6) {
      setFormError("Parola trebuie să aibă cel puțin 6 caractere.");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const res = await activateInvitation(activating.id, {
        password: password.trim(),
        ...(fullName.trim() ? { full_name: fullName.trim() } : {}),
      });
      setActivating(null);
      if (res.existing_account) {
        toast.success(res.message);
      } else {
        // Shown, not just toasted: this password exists nowhere else and the
        // admin has to pass it on.
        setDone({ email: res.email || activating.email, password: password.trim() });
      }
      await load();
      onChanged?.();
    } catch (e: any) {
      setFormError(e?.response?.data?.error || "Activarea nu a reușit.");
    }
    setBusy(false);
  };

  const cancel = async (inv: Invitation) => {
    if (!window.confirm(`Anulezi invitația pentru ${inv.email}?`)) return;
    try {
      await cancelInvitation(inv.id);
      toast.success("Invitație anulată.");
      await load();
      onChanged?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut anula invitația.");
    }
  };

  if (!clubId) return null;
  if (loading) return null;
  if (rows.length === 0 && !done) return null;

  const inputCls =
    "w-full px-3 py-2 text-base bg-white border rounded-lg border-slate-300 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500";

  return (
    <div className="bg-white border rounded-2xl border-amber-200">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-amber-100 bg-amber-50/60 rounded-t-2xl">
        <div>
          <h3 className="font-bold text-slate-800">{title}</h3>
          <p className="text-xs text-slate-500">
            Poți activa contul tu, fără să aștepți confirmarea pe email.
          </p>
        </div>
        <span className="px-2 py-0.5 text-xs font-bold text-amber-700 rounded-full bg-amber-100">
          {rows.length}
        </span>
      </div>

      {done && (
        <div className="px-4 py-3 border-b border-emerald-100 bg-emerald-50">
          <p className="text-sm font-semibold text-emerald-900">Cont activat</p>
          <p className="mt-1 text-sm text-emerald-800">
            Transmite-i datele — nu le-a primit pe email:
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-sm">
            <code className="px-2 py-1 bg-white border rounded border-emerald-200">{done.email}</code>
            <code className="px-2 py-1 bg-white border rounded border-emerald-200">{done.password}</code>
            <button
              onClick={() => {
                navigator.clipboard
                  ?.writeText(`Email: ${done.email}\nParolă: ${done.password}`)
                  .then(() => toast.success("Copiat."))
                  .catch(() => toast.error("Nu am putut copia."));
              }}
              className="font-semibold text-emerald-700 hover:text-emerald-900"
            >
              Copiază
            </button>
            <button onClick={() => setDone(null)} className="ml-auto font-semibold text-emerald-700">
              Închide
            </button>
          </div>
        </div>
      )}

      <div className="divide-y divide-slate-100">
        {rows.map((inv) => (
          <div key={inv.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
            <div className="flex-1 min-w-[200px]">
              <p className="font-medium break-all text-slate-800">{inv.email}</p>
              <p className="text-xs text-slate-500">
                {ROLE_LABEL[inv.role_code] || inv.role_code}
                {inv.sport_name ? ` · ${inv.sport_name}` : ""}
                {inv.team?.name ? ` · ${inv.team.name}` : ""}
                {inv.athlete_name ? ` · pentru ${inv.athlete_name}` : ""}
              </p>
              <p className="text-[11px] text-slate-400">
                Trimisă de {inv.invited_by?.full_name || inv.invited_by?.email || "—"} · expiră{" "}
                {formatDate(inv.expires_at)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* A parent creates their own child, so there is nothing for the
                  club to activate on their behalf. */}
              {inv.role_code !== "parent" && (
                <button
                  onClick={() => openActivate(inv)}
                  className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                >
                  Activează contul
                </button>
              )}
              <button
                onClick={() => cancel(inv)}
                className="px-3 py-1.5 text-xs font-semibold border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Anulează
              </button>
            </div>
          </div>
        ))}
      </div>

      {activating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md p-6 bg-white shadow-xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-slate-800">Activează contul</h3>
            <p className="mt-1 text-sm text-slate-500">
              {activating.email} · {ROLE_LABEL[activating.role_code] || activating.role_code}
            </p>

            <div className="p-3 mt-4 text-sm rounded-xl bg-slate-50 text-slate-600">
              Contul va fi complet activ: adresa de email e considerată confirmată, iar persoana se
              poate autentifica imediat. Nu mai primește niciun email de confirmare, deci parola de
              mai jos trebuie transmisă de tine.
            </div>

            <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">
              Nume complet (opțional)
            </label>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="ex. Andrei Pop"
              className={inputCls}
            />

            <label className="block mt-4 mb-1 text-xs font-semibold text-slate-500">Parolă *</label>
            <div className="flex gap-2">
              <input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
              <button
                onClick={() => setPassword(suggestPassword())}
                type="button"
                className="px-3 py-2 text-xs font-semibold border rounded-lg whitespace-nowrap border-slate-300 text-slate-600 hover:bg-slate-50"
              >
                Generează
              </button>
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
              Minim 6 caractere. Persoana o poate schimba oricând din contul ei.
            </p>

            {formError && <p className="mt-3 text-sm text-rose-600">{formError}</p>}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setActivating(null)}
                disabled={busy}
                className="px-4 py-2 text-sm font-semibold text-slate-600 hover:text-slate-800 disabled:opacity-50"
              >
                Anulează
              </button>
              <button
                onClick={submit}
                disabled={busy || password.trim().length < 6}
                className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
              >
                {busy ? "Se activează..." : "Activează"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Readable and typeable: this gets dictated over the phone. */
function suggestPassword(): string {
  const words = ["Sport", "Echipa", "Antrenor", "Clubul", "Meciul", "Trofeu", "Victorie"];
  const word = words[Math.floor(Math.random() * words.length)];
  const digits = String(Math.floor(100 + Math.random() * 900));
  return `${word}${digits}`;
}
